/*
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

import {GenericModelViewerListNode} from "../../../Components/Common/GenericModelViewerListNode";
import {MutableList} from "../../../Components/Common/List/MutableList";
import {NonMutableList} from "../../../Components/Common/List/NonMutableList";
import {GuidUtils} from "../../../Utils";
import React, {useCallback, useEffect, useMemo} from "react";
import * as listComponents from "../../../Components/Common/List/ListComponents";

export const ChildrenFieldEditor = (props) => {
	
	const { FieldDef, Value, RootData, OnValidationUpdate, CustomData, OnUpdate } = props;
	
	const { model, nonMutable, orderable, sortKey, listComponentOverride, customData } = FieldDef.association;
	
	const primaryKeyName = model.getPrimaryKeyName();
	
	const defaultComponentName =
		orderable ? 'OrderableMutableList' :
			(nonMutable ? 'NonMutableList' : 'MutableList');
	
	// note: listComponentOverride is usually set to false when not overridden
	const componentName = listComponentOverride || defaultComponentName;
	
	const ListComponent = listComponents?.[componentName] ?? null;
	
	if (!ListComponent) {
		console.log(`ListComponent with name: '${componentName}' was not defined.`);
		return null;
	}
	
	const findItemByKey = (key, items) => {
		const stringKey = String(key);
		return items.find((x) => String(x[primaryKeyName]) === stringKey) ||
			items.find((x) => String(x['_tempId']) === stringKey);
	};
	
	/**
	 * Finds the index of an item in the list by its key or object.
	 * Returns the index of the item if found, otherwise -1.
	 * @param keyOrObject
	 * @param items
	 * @returns {*}
	 */
	const findItemIndex = (keyOrObject, items) => {
		const stringKeyValue = (typeof keyOrObject === 'object') ?
			String(keyOrObject?.[primaryKeyName] ?? keyOrObject?.['_tempId']) :
			String(keyOrObject);
		
		const item = findItemByKey(stringKeyValue, items);
		const index = items.indexOf(item);
		return index;
	}
	
	const updateItem = useCallback((item) => {
		OnUpdate(prevItems => {
			const items = prevItems ?? [];
			const index = findItemIndex(item, items);
			if (index === -1) return items;
			const newItems = [...items];
			newItems[index] = item;
			return newItems;
		});
	}, [OnUpdate])

	const removeItem = useCallback((keyOrObject) => {
		OnUpdate(prevItems => {
			const items = prevItems ?? [];
			const index = findItemIndex(keyOrObject, items);
			if (index === -1) return items;
			const newItems = [...items];
			newItems.splice(index, 1);
			return newItems;
		});
	}, [OnUpdate])

	const addItem = useCallback((item = null) => {
		// Generate tempId outside the updater so it's stable if React batches
		const newItemBase = {
			...(FieldDef.model?.defaults ?? FieldDef.association.model?.defaults),
			...item ?? {},
			_tempId: GuidUtils.Generate(),
		};

		OnUpdate(prevItems => {
			const items = prevItems ?? [];
			const newItem = { ...newItemBase };

			// Ensure new items are always appended at the bottom of an ordered list
			if (FieldDef.association.orderable) {
				let maxOrder = 0;
				items.forEach((x) => {
					if (x[FieldDef.association.orderable] > maxOrder) {
						maxOrder = x[FieldDef.association.orderable];
					}
				});
				newItem[FieldDef.association.orderable] = ++maxOrder;
			}

			return [...items, newItem];
		});
	}, [FieldDef.model?.defaults, FieldDef.association.model?.defaults, FieldDef.association.orderable, OnUpdate]);
	
	// Merge CustomData separately so RootData changing (every keystroke) doesn't
	// create a new CustomData object reference and blow past React.memo in all
	// the list nodes — CustomData only changes when its actual deps change.
	const mergedCustomData = useMemo(() => ({
		...CustomData,
		...customData,
	}), [CustomData, customData]);

	// RootData intentionally omitted: it's the root survey object and changes on
	// every keystroke.  The cross-field visibility rules in SurveyQuestion / SurveySection
	// only reference fields on the item itself (e.g. surveyQuestionDataTypeId), so each
	// child GMV can use its own Data as the visibility root.  Including RootData here
	// caused additionalTypeProps to be a new object on every keystroke, defeating the
	// React.memo check in GenericModelViewerListNode and re-rendering all list nodes.
	// Consequence: __RootData is undefined in list items' visibility queries — context
	// from outside the item must ride CustomData (stable reference, merged below).
	const additionalTypeProps = useMemo(() => ({
		FieldDef,
		Editable: true,
		OnValidationUpdate,
		CustomData: mergedCustomData,
	}), [FieldDef, OnValidationUpdate, mergedCustomData]);
	
	return (
        /**
         * Default ListComponent is generally MutableList or OrderableMutableList
         */
		<ListComponent
			Editable={true}
			DataTypeName={FieldDef.title}
			Data={Value}
			Type={GenericModelViewerListNode} // Ensure GenericModelViewerListNode is imported or defined
			AdditionalTypeProps={additionalTypeProps}
			RootData={RootData}
			PrimaryKeyName={primaryKeyName}
			sortKey={sortKey ?? orderable}
			UpdateItems={OnUpdate}
			UpdateItem={updateItem}
			AddItem={addItem}
			RemoveItem={removeItem}
		/>
	);
};
